import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../api/client';

function AdminDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-blue-900 to-blue-800 text-white shadow-2xl">
        <div className="p-8 border-b border-blue-700">
          <h1 className="text-3xl font-extrabold">📊 Admin</h1>
          <p className="text-blue-200 text-sm mt-3">{user?.full_name}</p>
          <span className="inline-block mt-2 px-3 py-1 bg-blue-600 rounded-full text-xs font-semibold">
            {user?.role?.toUpperCase()}
          </span>
        </div>
        
        <nav className="mt-8 space-y-2 px-4">
          <SidebarLink to="/admin/dashboard" icon="📊" label="Dashboard" />
          <SidebarLink to="/admin/pages" icon="📄" label="Sayfa Yönetimi" />
          <SidebarLink to="/admin/categories" icon="📁" label="Kategoriler" />
          <SidebarLink to="/admin/announcements" icon="📢" label="Duyurular" />
          <SidebarLink to="/admin/contacts" icon="📞" label="İletişim Talepleri" />
          <SidebarLink to="/admin/orders" icon="🛒" label="Siparişler" />
          <SidebarLink to="/admin/users" icon="👥" label="Personel" />
          <SidebarLink to="/admin/branches" icon="🏢" label="Şubeler" />
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-md px-8 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Admin Kontrol Paneli</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Şube: {user?.branch_id}</span>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition flex items-center gap-1"
            >
              🚪 Çıkış
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-8">
            <Routes>
              <Route path="/dashboard" element={<AdminDashboardHome />} />
              <Route path="/pages" element={<AdminPages />} />
              <Route path="/pages/:pageId" element={<AdminPageImages />} />
              <Route path="/categories" element={<AdminCategories />} />
              <Route path="/announcements" element={<AdminAnnouncements />} />
              <Route path="/contacts" element={<AdminContacts />} />
              <Route path="/orders" element={<AdminOrders />} />
              <Route path="/users" element={<AdminUsers />} />
              <Route path="/branches" element={<AdminBranches />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ to, icon, label }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-medium"
    >
      <span className="text-xl">{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

function AdminDashboardHome() {
  return (
    <div>
      <h3 className="text-3xl font-bold text-gray-800 mb-8">Genel Bakış</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <DashboardCard title="Toplam Ürün" value="0" icon="📦" color="blue" />
        <DashboardCard title="Beklemede" value="0" icon="⏳" color="yellow" />
        <DashboardCard title="Hazır" value="0" icon="✅" color="green" />
        <DashboardCard title="Personel" value="5" icon="👥" color="purple" />
      </div>
    </div>
  );
}

function AdminAnnouncements() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  React.useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/announcements/admin/all');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Duyurular yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      alert('Başlık ve içerik boş olamaz!');
      return;
    }

    if (!publishDate) {
      alert('Yayınlanma tarihi seçmelisiniz!');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('publish_date', publishDate);
      if (endDate) {
        formData.append('end_date', endDate);
      }
      if (image) {
        formData.append('image', image);
      }

      await apiClient.post('/announcements', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccessMessage('✅ Duyuru başarıyla kaydedildi!');
      setTitle('');
      setContent('');
      setPublishDate('');
      setEndDate('');
      setImage(null);
      setImagePreview(null);
      fetchAnnouncements();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Duyuru yayınlanamadı:', error);
      alert('Duyuru yayınlanırken hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) return;

    try {
      await apiClient.delete(`/announcements/${id}`);
      setAnnouncements(announcements.filter(a => a.id !== id));
      setSuccessMessage('✅ Duyuru silindi!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Duyuru silinemedi:', error);
      alert('Duyuru silinirken hata oluştu');
    }
  };

  const getStatusBadge = (announcement) => {
    if (announcement.status === 'scheduled') {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">⏳ Planlandı</span>;
    } else if (announcement.status === 'expired') {
      return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">⌛ Süresi Doldu</span>;
    } else {
      return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">✅ Aktif</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-8">
      <h3 className="text-3xl font-bold text-gray-800">📢 Duyuru Yönetimi</h3>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-8">
        <h4 className="text-2xl font-bold mb-6 text-gray-800">Yeni Duyuru Oluştur</h4>
        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
            {successMessage}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Başlık
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Duyuru başlığını girin..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              İçerik
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Duyuru içeriğini girin..."
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Tarih Seçimi */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📅 Yayınlanma Tarihi *
              </label>
              <input
                type="datetime-local"
                value={publishDate}
                onChange={(e) => setPublishDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Duyuru bu tarihte otomatik yayınlanır</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🏁 Bitiş Tarihi (Opsiyonel)
              </label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={publishDate}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Boş bırakılırsa süresiz yayınlanır</p>
            </div>
          </div>
          
          {/* Görsel Yükleme */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🖼️ Duyuru Görseli (İsteğe Bağlı)
            </label>
            {!imagePreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition cursor-pointer"
                   onClick={() => document.getElementById('announcement-image').click()}>
                <input
                  id="announcement-image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className="text-4xl mb-2">📷</div>
                <p className="text-gray-500">Görsel yüklemek için tıklayın</p>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF - Maks 5MB</p>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Önizleme"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          
          <button
            type="submit"
            className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition"
          >
            💾 Duyuruyu Kaydet
          </button>
        </form>
      </div>

      {/* Announcements List */}
      <div className="bg-white rounded-lg shadow-md p-8">
        <h4 className="text-2xl font-bold mb-6 text-gray-800">Tüm Duyurular</h4>
        {loading ? (
          <p className="text-center text-gray-500">Yükleniyor...</p>
        ) : announcements.length === 0 ? (
          <p className="text-center text-gray-500">Henüz duyuru yok</p>
        ) : (
          <div className="grid gap-4">
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className={`border rounded-lg overflow-hidden hover:shadow-md transition ${
                  announcement.status === 'expired' ? 'border-gray-300 bg-gray-50 opacity-60' :
                  announcement.status === 'scheduled' ? 'border-yellow-300 bg-yellow-50' :
                  'border-green-300 bg-green-50'
                }`}
              >
                <div className="flex">
                  {announcement.image_url && (
                    <img
                      src={`http://localhost:5000${announcement.image_url}`}
                      alt={announcement.title}
                      className="w-32 h-32 object-cover"
                    />
                  )}
                  <div className="flex-1 p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <h5 className="text-lg font-bold text-gray-800">{announcement.title}</h5>
                        {getStatusBadge(announcement)}
                      </div>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded transition"
                      >
                        🗑️ Sil
                      </button>
                    </div>
                    <p className="text-gray-700 mb-2 text-sm line-clamp-2">{announcement.content}</p>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span>📅 Başlangıç: {formatDate(announcement.publish_date)}</span>
                      <span>🏁 Bitiş: {formatDate(announcement.end_date)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DashboardCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
  };

  return (
    <div className={`${colorClasses[color]} border-l-4 rounded-lg p-6 shadow-md hover:shadow-lg transition`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-semibold">{title}</p>
          <p className="text-4xl font-bold text-gray-800 mt-2">{value}</p>
        </div>
        <span className="text-4xl">{icon}</span>
      </div>
    </div>
  );
}

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await apiClient.get('/shop/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await apiClient.patch(`/shop/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      alert('Durum güncellenemedi');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Beklemede', class: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      preparing: { text: 'Hazırlanıyor', class: 'bg-blue-100 text-blue-800', icon: '⚙️' },
      ready: { text: 'Hazır', class: 'bg-green-100 text-green-800', icon: '✅' },
      completed: { text: 'Tamamlandı', class: 'bg-gray-100 text-gray-800', icon: '📦' },
      cancelled: { text: 'İptal', class: 'bg-red-100 text-red-800', icon: '❌' },
    };
    return badges[status] || badges.pending;
  };

  const filteredOrders = statusFilter 
    ? orders.filter(o => o.status === statusFilter)
    : orders;

  if (loading) return <p className="text-center py-8">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-3xl font-bold text-gray-800">🛒 Siparişler</h3>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Tüm Durumlar</option>
            <option value="pending">⏳ Beklemede</option>
            <option value="preparing">⚙️ Hazırlanıyor</option>
            <option value="ready">✅ Hazır</option>
            <option value="completed">📦 Tamamlandı</option>
            <option value="cancelled">❌ İptal</option>
          </select>
          <button 
            onClick={fetchOrders}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            🔄 Yenile
          </button>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-5 gap-4">
        {['pending', 'preparing', 'ready', 'completed', 'cancelled'].map(status => {
          const badge = getStatusBadge(status);
          const count = orders.filter(o => o.status === status).length;
          return (
            <div 
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              className={`${badge.class} p-4 rounded-lg text-center cursor-pointer transition hover:opacity-80 ${
                statusFilter === status ? 'ring-2 ring-offset-2 ring-gray-400' : ''
              }`}
            >
              <span className="text-2xl">{badge.icon}</span>
              <p className="font-bold text-2xl">{count}</p>
              <p className="text-sm">{badge.text}</p>
            </div>
          );
        })}
      </div>

      {/* Sipariş Listesi */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sol Panel - Sipariş Listesi */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b font-bold text-gray-700">
            📋 Sipariş Listesi ({filteredOrders.length})
          </div>
          <div className="divide-y max-h-[600px] overflow-auto">
            {filteredOrders.length === 0 ? (
              <p className="p-8 text-center text-gray-500">Sipariş bulunamadı</p>
            ) : (
              filteredOrders.map((order) => {
                const badge = getStatusBadge(order.status);
                return (
                  <div 
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                      selectedOrder?.id === order.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-gray-800">#{order.id} - {order.order_number}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.class}`}>
                        {badge.icon} {badge.text}
                      </span>
                    </div>
                    <p className="font-semibold text-gray-700">{order.customer_name}</p>
                    <p className="text-sm text-gray-500">{order.customer_phone}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleString('tr-TR')}
                      </span>
                      <span className="font-bold text-green-600">{order.total_amount} ₺</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Sağ Panel - Sipariş Detay */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b font-bold text-gray-700">
            📦 Sipariş Detayı
          </div>
          {selectedOrder ? (
            <div className="p-6">
              {/* Müşteri Bilgileri */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-2">👤 Müşteri Bilgileri</h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                  <p><strong>Ad:</strong> {selectedOrder.customer_name}</p>
                  <p><strong>Telefon:</strong> {selectedOrder.customer_phone}</p>
                  {selectedOrder.customer_email && (
                    <p><strong>E-posta:</strong> {selectedOrder.customer_email}</p>
                  )}
                  {selectedOrder.customer_address && (
                    <p><strong>Adres:</strong> {selectedOrder.customer_address}</p>
                  )}
                  {selectedOrder.notes && (
                    <p><strong>Notlar:</strong> {selectedOrder.notes}</p>
                  )}
                </div>
              </div>

              {/* Durum Güncelleme */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-2">🔄 Durum Güncelle</h4>
                <div className="flex gap-2 flex-wrap">
                  {['pending', 'preparing', 'ready', 'completed', 'cancelled'].map(status => {
                    const badge = getStatusBadge(status);
                    return (
                      <button
                        key={status}
                        onClick={() => updateStatus(selectedOrder.id, status)}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                          selectedOrder.status === status
                            ? `${badge.class} ring-2 ring-offset-1`
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {badge.icon} {badge.text}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Ürünler */}
              <div>
                <h4 className="font-bold text-lg mb-2">🛍️ Sipariş Ürünleri</h4>
                <div className="space-y-3 max-h-64 overflow-auto">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex gap-4 bg-gray-50 p-3 rounded-lg">
                      <img
                        src={`http://localhost:5000${item.image_url}`}
                        alt={item.title}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h5 className="font-bold text-gray-800">{item.title}</h5>
                        {item.barcode && (
                          <span className="text-xs font-mono text-gray-500">🏷️ {item.barcode}</span>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {item.quantity} adet
                          </span>
                          {item.price && (
                            <span className="text-green-600 font-bold">{item.price} ₺</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="font-bold text-lg">Toplam:</span>
                  <span className="text-2xl font-bold text-green-600">{selectedOrder.total_amount} ₺</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <span className="text-6xl block mb-4">👈</span>
              <p>Detaylarını görmek için bir sipariş seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== PERSONEL YÖNETİMİ ====================
function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'staff',
    branch_id: ''
  });
  const { user: currentUser } = useContext(AuthContext);

  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Personel yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await apiClient.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const { password, ...updateData } = formData;
        await apiClient.put(`/users/${editingUser.id}`, updateData);
      } else {
        await apiClient.post('/users', formData);
      }
      fetchUsers();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.error || 'Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu personeli silmek istediğinize emin misiniz?')) {
      try {
        await apiClient.delete(`/users/${id}`);
        fetchUsers();
      } catch (error) {
        alert(error.response?.data?.error || 'Silme işlemi başarısız');
      }
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Şifre en az 6 karakter olmalıdır');
      return;
    }
    try {
      await apiClient.patch(`/users/${selectedUserId}/password`, { password: newPassword });
      alert('Şifre başarıyla güncellendi');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUserId(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Şifre güncellenemedi');
    }
  };

  const resetForm = () => {
    setFormData({ username: '', password: '', full_name: '', email: '', role: 'staff', branch_id: '' });
    setEditingUser(null);
    setShowForm(false);
  };

  const startEdit = (user) => {
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name,
      email: user.email || '',
      role: user.role,
      branch_id: user.branch_id || ''
    });
    setEditingUser(user);
    setShowForm(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-3xl font-bold text-gray-800">👥 Personel Yönetimi</h3>
        <button
          onClick={() => setShowForm(true)}
          className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition"
        >
          + Yeni Personel
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h4 className="text-xl font-bold mb-6">{editingUser ? 'Personel Düzenle' : 'Yeni Personel'}</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Adı</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={editingUser}
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Şifre</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                    minLength={6}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="ornek@bilisim.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="staff">Personel</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şube</label>
                <select
                  value={formData.branch_id}
                  onChange={(e) => setFormData({...formData, branch_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Şube Seçin</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {editingUser ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h4 className="text-xl font-bold mb-6">🔑 Şifre Sıfırla</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="En az 6 karakter"
                  minLength={6}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowPasswordModal(false); setNewPassword(''); }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  onClick={handlePasswordReset}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  Şifreyi Güncelle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Kullanıcı Adı</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Ad Soyad</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Rol</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Şube</th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map(user => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium">{user.username}</td>
                <td className="px-6 py-4">{user.full_name}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user.role === 'admin' ? 'Admin' : 'Personel'}
                  </span>
                </td>
                <td className="px-6 py-4">{user.branch_name || '-'}</td>
                <td className="px-6 py-4">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => startEdit(user)}
                      className="px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 text-sm"
                    >
                      ✏️ Düzenle
                    </button>
                    <button
                      onClick={() => { setSelectedUserId(user.id); setShowPasswordModal(true); }}
                      className="px-3 py-1 bg-yellow-100 text-yellow-600 rounded hover:bg-yellow-200 text-sm"
                    >
                      🔑 Şifre
                    </button>
                    {user.id !== currentUser?.id && (
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 text-sm"
                      >
                        🗑️ Sil
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="text-center py-12 text-gray-500">Henüz personel bulunmuyor</div>
        )}
      </div>
    </div>
  );
}

// ==================== ŞUBE YÖNETİMİ ====================
function AdminBranches() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await apiClient.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await apiClient.put(`/branches/${editingBranch.id}`, formData);
      } else {
        await apiClient.post('/branches', formData);
      }
      fetchBranches();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.error || 'Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu şubeyi silmek istediğinize emin misiniz?')) {
      try {
        await apiClient.delete(`/branches/${id}`);
        fetchBranches();
      } catch (error) {
        alert(error.response?.data?.error || 'Silme işlemi başarısız');
      }
    }
  };

  const toggleStatus = async (branch) => {
    try {
      await apiClient.patch(`/branches/${branch.id}/toggle`);
      fetchBranches();
    } catch (error) {
      alert(error.response?.data?.error || 'Durum değiştirilemedi');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', phone: '', email: '' });
    setEditingBranch(null);
    setShowForm(false);
  };

  const startEdit = (branch) => {
    setFormData({
      name: branch.name,
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || ''
    });
    setEditingBranch(branch);
    setShowForm(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-3xl font-bold text-gray-800">🏢 Şube Yönetimi</h3>
        <button
          onClick={() => setShowForm(true)}
          className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition"
        >
          + Yeni Şube
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h4 className="text-xl font-bold mb-6">{editingBranch ? 'Şube Düzenle' : 'Yeni Şube'}</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şube Adı</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {editingBranch ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Branches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {branches.map(branch => (
          <div key={branch.id} className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${branch.is_active ? 'border-green-500' : 'border-gray-400'}`}>
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-xl font-bold text-gray-800">{branch.name}</h4>
              <span className={`px-2 py-1 rounded text-xs font-semibold ${branch.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {branch.is_active ? 'Aktif' : 'Pasif'}
              </span>
            </div>
            
            {branch.address && (
              <p className="text-gray-600 text-sm mb-2">📍 {branch.address}</p>
            )}
            {branch.phone && (
              <p className="text-gray-600 text-sm mb-2">📞 {branch.phone}</p>
            )}
            {branch.email && (
              <p className="text-gray-600 text-sm mb-2">✉️ {branch.email}</p>
            )}
            
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500 mb-3">
                👥 <span className="font-semibold">{branch.user_count || 0}</span> Personel
              </p>
              
              <div className="flex gap-2">
                <button
                  onClick={() => startEdit(branch)}
                  className="flex-1 px-3 py-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 text-sm"
                >
                  ✏️ Düzenle
                </button>
                <button
                  onClick={() => toggleStatus(branch)}
                  className={`flex-1 px-3 py-1.5 rounded text-sm ${
                    branch.is_active 
                      ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200' 
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                >
                  {branch.is_active ? '⏸️ Pasif Yap' : '▶️ Aktif Yap'}
                </button>
                <button
                  onClick={() => handleDelete(branch.id)}
                  className="px-3 py-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 text-sm"
                  title={branch.user_count > 0 ? 'Personel atanmış şube silinemez' : 'Sil'}
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {branches.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow">
          Henüz şube bulunmuyor
        </div>
      )}
    </div>
  );
}

// ==================== SAYFA YÖNETİMİ ====================
const PAGE_ICONS = ['📄', '📝', '💻', '📚', '🛒', '📦', '🎨', '🔧', '📱', '🖥️', '⌨️', '🖨️', '💼', '📋', '🎮', '🎧', '📸', '🔌', '💡', '🛠️'];

function AdminPages() {
  const [pages, setPages] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({ slug: '', title: '', description: '', icon: '📄' });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedPageForCategories, setSelectedPageForCategories] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPages();
    fetchAllCategories();
  }, []);

  const fetchAllCategories = async () => {
    try {
      const response = await apiClient.get('/categories');
      setAllCategories(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await apiClient.get('/pages');
      setPages(response.data);
    } catch (error) {
      console.error('Sayfalar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ slug: '', title: '', description: '', icon: '📄' });
    setEditingPage(null);
    setShowForm(false);
  };

  const startEdit = (page) => {
    setFormData({
      slug: page.slug,
      title: page.title,
      description: page.description || '',
      icon: page.icon || '📄'
    });
    setEditingPage(page);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await apiClient.put(`/pages/${editingPage.id}`, formData);
      } else {
        await apiClient.post('/pages', formData);
      }
      resetForm();
      fetchPages();
    } catch (error) {
      alert(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const togglePage = async (id) => {
    try {
      await apiClient.patch(`/pages/${id}/toggle`);
      fetchPages();
    } catch (error) {
      console.error('Sayfa durumu değiştirilemedi:', error);
    }
  };

  const deletePage = async (id) => {
    if (!window.confirm('Bu sayfayı silmek istediğinize emin misiniz?')) return;
    try {
      await apiClient.delete(`/pages/${id}`);
      fetchPages();
    } catch (error) {
      console.error('Sayfa silinemedi:', error);
    }
  };

  const openCategoryModal = async (page) => {
    setSelectedPageForCategories(page);
    try {
      const response = await apiClient.get(`/pages/${page.id}/categories`);
      setSelectedCategoryIds(response.data.flat.map(c => c.id));
    } catch (error) {
      setSelectedCategoryIds([]);
    }
    setShowCategoryModal(true);
  };

  const toggleCategorySelection = (catId) => {
    setSelectedCategoryIds(prev => 
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const saveCategoryAssignments = async () => {
    try {
      await apiClient.post(`/pages/${selectedPageForCategories.id}/categories`, {
        category_ids: selectedCategoryIds
      });
      setShowCategoryModal(false);
      setSelectedPageForCategories(null);
      alert('Kategoriler kaydedildi!');
    } catch (error) {
      alert('Kategoriler kaydedilemedi');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-3xl font-bold text-gray-800">📄 Sayfa Yönetimi</h3>
        <button
          onClick={() => showForm ? resetForm() : setShowForm(true)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          {showForm ? '✕ İptal' : '+ Yeni Sayfa'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-xl font-bold mb-4">{editingPage ? '✏️ Sayfa Düzenle' : 'Yeni Sayfa Oluştur'}</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">İkon</label>
                <div className="flex flex-wrap gap-1 p-2 border rounded-lg max-h-24 overflow-y-auto">
                  {PAGE_ICONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`w-8 h-8 text-lg flex items-center justify-center rounded hover:bg-gray-100 ${
                        formData.icon === icon ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Slug (URL)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="ornek-sayfa"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Başlık</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Sayfa Başlığı"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Açıklama</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Sayfa açıklaması..."
                rows="2"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              {editingPage ? '✓ Güncelle' : '✓ Oluştur'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <p className="p-6 text-center text-gray-500">Yükleniyor...</p>
        ) : pages.length === 0 ? (
          <p className="p-6 text-center text-gray-500">Henüz sayfa yok</p>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-16">İkon</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Sayfa</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Slug</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Görsel</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Durum</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {pages.map((page) => (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-center text-2xl">{page.icon || '📄'}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-800">{page.title}</p>
                      <p className="text-sm text-gray-500">{page.description}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">/{page.slug}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {page.image_count || 0} görsel
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => togglePage(page.id)}
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        page.is_active 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {page.is_active ? '✓ Aktif' : '✕ Pasif'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center space-x-2">
                    <button
                      onClick={() => openCategoryModal(page)}
                      className="px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm"
                    >
                      📁 Kategoriler
                    </button>
                    <button
                      onClick={() => startEdit(page)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                    >
                      ✏️ Düzenle
                    </button>
                    <button
                      onClick={() => navigate(`/admin/pages/${page.id}`)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      📷 Görseller
                    </button>
                    <button
                      onClick={() => deletePage(page.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      🗑️ Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Kategori Atama Modal */}
      {showCategoryModal && selectedPageForCategories && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <h4 className="text-xl font-bold mb-4">
              📁 {selectedPageForCategories.title} - Kategori Ata
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Bu sayfaya atanacak kategorileri seçin. Ürün eklerken sadece bu kategoriler görünecek.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
              {allCategories.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Henüz kategori yok</p>
              ) : (
                allCategories.map((cat) => (
                  <label 
                    key={cat.id} 
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                      selectedCategoryIds.includes(cat.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds.includes(cat.id)}
                      onChange={() => toggleCategorySelection(cat.id)}
                      className="w-5 h-5 text-blue-600"
                    />
                    <span className="text-xl">{cat.icon || '📁'}</span>
                    <span className="font-medium">{cat.name}</span>
                    {cat.parent_name && (
                      <span className="text-xs text-gray-500">(Alt: {cat.parent_name})</span>
                    )}
                  </label>
                ))
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                İptal
              </button>
              <button
                onClick={saveCategoryAssignments}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                ✓ Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== SAYFA GÖRSELLERİ ====================
function AdminPageImages() {
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', price: '', category_id: '', barcode: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [editFormData, setEditFormData] = useState({ title: '', description: '', price: '', category_id: '', barcode: '' });
  const navigate = useNavigate();
  
  // URL'den pageId al
  const pageId = window.location.pathname.split('/').pop();

  useEffect(() => {
    fetchPageAndImages();
    fetchCategories();
  }, [pageId]);

  const fetchCategories = async () => {
    try {
      // Sadece bu sayfaya atanmış kategorileri çek
      const response = await apiClient.get(`/pages/${pageId}/categories`);
      setCategories(response.data.flat || []);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
      setCategories([]);
    }
  };

  const fetchPageAndImages = async () => {
    try {
      const [pagesRes, imagesRes] = await Promise.all([
        apiClient.get('/pages'),
        apiClient.get(`/pages/${pageId}/images`)
      ]);
      const currentPage = pagesRes.data.find(p => p.id === parseInt(pageId));
      setPage(currentPage);
      setImages(imagesRes.data);
    } catch (error) {
      console.error('Veriler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Lütfen bir görsel seçin');
      return;
    }

    setUploading(true);
    const form = new FormData();
    form.append('image', selectedFile);
    form.append('title', formData.title);
    form.append('description', formData.description);
    form.append('price', formData.price);
    form.append('barcode', formData.barcode);
    if (formData.category_id) {
      form.append('category_id', formData.category_id);
    }

    try {
      await apiClient.post(`/pages/${pageId}/images`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ title: '', description: '', price: '', category_id: '', barcode: '' });
      setSelectedFile(null);
      fetchPageAndImages();
    } catch (error) {
      if (error.response?.data?.existingProduct) {
        alert(`Bu barkod başka bir üründe kullanılıyor: "${error.response.data.existingProduct.title}"`);
      } else {
        alert(error.response?.data?.error || 'Görsel yüklenemedi');
      }
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('Bu görseli silmek istediğinize emin misiniz?')) return;
    try {
      await apiClient.delete(`/pages/images/${id}`);
      fetchPageAndImages();
    } catch (error) {
      console.error('Görsel silinemedi:', error);
    }
  };

  const startEditImage = (image) => {
    setEditingImage(image);
    setEditFormData({
      title: image.title || '',
      description: image.description || '',
      price: image.price || '',
      category_id: image.category_id || '',
      barcode: image.barcode || ''
    });
  };

  const handleUpdateImage = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/pages/images/${editingImage.id}`, editFormData);
      setEditingImage(null);
      fetchPageAndImages();
    } catch (error) {
      if (error.response?.data?.existingProduct) {
        alert(`Bu barkod başka bir üründe kullanılıyor: "${error.response.data.existingProduct.title}"`);
      } else {
        alert(error.response?.data?.error || 'Ürün güncellenemedi');
      }
    }
  };

  if (loading) return <p className="text-center py-8">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/pages')} className="text-blue-600 hover:text-blue-800">
          ← Geri
        </button>
        <h3 className="text-3xl font-bold text-gray-800">
          📷 {page?.title} - Görseller
        </h3>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-xl font-bold mb-4">Yeni Görsel Yükle</h4>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Barkod</label>
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                placeholder="Örn: BRS001"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Başlık</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ürün adı"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Fiyat (₺)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="0.00"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Kategori</label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">Kategori Seçin</option>
                {categories.length === 0 ? (
                  <option disabled>Bu sayfaya kategori atanmamış</option>
                ) : (
                  categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon || '📁'} {cat.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Görsel</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Açıklama</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ürün açıklaması..."
              rows="2"
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>
          <button 
            type="submit" 
            disabled={uploading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {uploading ? '⏳ Yükleniyor...' : '📤 Yükle'}
          </button>
        </form>
      </div>

      {/* Images Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.length === 0 ? (
          <p className="col-span-full text-center text-gray-500 py-8">Henüz görsel yok</p>
        ) : (
          images.map((image) => (
            <div key={image.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <img
                src={`http://localhost:5000${image.image_url}`}
                alt={image.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                {image.barcode && (
                  <span className="inline-block mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded font-mono">
                    🏷️ {image.barcode}
                  </span>
                )}
                <h5 className="font-bold text-gray-800">{image.title || 'Başlıksız'}</h5>
                <p className="text-sm text-gray-600 mt-1">{image.description}</p>
                {image.category_name && (
                  <span className="inline-block mt-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {image.category_icon || '📁'} {image.category_name}
                  </span>
                )}
                {image.price && (
                  <p className="text-xl font-bold text-green-600 mt-2">{image.price} ₺</p>
                )}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => startEditImage(image)}
                    className="px-4 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                  >
                    ✏️ Düzenle
                  </button>
                  <button
                    onClick={() => deleteImage(image.id)}
                    className="px-4 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    🗑️ Sil
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Ürün Düzenleme Modal */}
      {editingImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h4 className="text-xl font-bold mb-4">✏️ Ürün Düzenle</h4>
            <form onSubmit={handleUpdateImage} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Barkod</label>
                <input
                  type="text"
                  value={editFormData.barcode}
                  onChange={(e) => setEditFormData({ ...editFormData, barcode: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg font-mono"
                  placeholder="Örn: BRS001"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Başlık</label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Fiyat (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.price}
                  onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Kategori</label>
                <select
                  value={editFormData.category_id}
                  onChange={(e) => setEditFormData({ ...editFormData, category_id: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon || '📁'} {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Açıklama</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setEditingImage(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  ✓ Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== İLETİŞİM TALEPLERİ ====================
function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await apiClient.get('/contact');
      setContacts(response.data);
    } catch (error) {
      console.error('İletişim talepleri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await apiClient.put(`/contact/${id}`, { status });
      fetchContacts();
    } catch (error) {
      console.error('Durum güncellenemedi:', error);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Bu talebi silmek istediğinize emin misiniz?')) return;
    try {
      await apiClient.delete(`/contact/${id}`);
      fetchContacts();
    } catch (error) {
      console.error('Talep silinemedi:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Beklemede', class: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      contacted: { text: 'İletişime Geçildi', class: 'bg-blue-100 text-blue-800', icon: '📞' },
      resolved: { text: 'Çözüldü', class: 'bg-green-100 text-green-800', icon: '✅' }
    };
    return badges[status] || badges.pending;
  };

  const filteredContacts = statusFilter
    ? contacts.filter(c => c.status === statusFilter)
    : contacts;

  if (loading) return <p className="text-center py-8">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-3xl font-bold text-gray-800">📞 İletişim Talepleri</h3>
        <button 
          onClick={fetchContacts}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          🔄 Yenile
        </button>
      </div>

      {/* Durum Filtreleme Kartları */}
      <div className="grid grid-cols-3 gap-4">
        {['pending', 'contacted', 'resolved'].map(status => {
          const badge = getStatusBadge(status);
          const count = contacts.filter(c => c.status === status).length;
          return (
            <div 
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              className={`${badge.class} p-6 rounded-xl text-center cursor-pointer transition-all hover:opacity-80 hover:scale-105 ${statusFilter === status ? 'ring-4 ring-offset-2 ring-gray-400 scale-105' : ''}`}
            >
              <span className="text-4xl block mb-2">{badge.icon}</span>
              <p className="font-bold text-3xl">{count}</p>
              <p className="text-sm font-semibold mt-1">{badge.text}</p>
            </div>
          );
        })}
      </div>

      {/* Toplam ve Aktif Filtre */}
      <div className="flex items-center justify-between">
        <p className="text-gray-600">
          {statusFilter ? (
            <span>Filtrelenen: <strong>{filteredContacts.length}</strong> / {contacts.length} talep</span>
          ) : (
            <span>Toplam: <strong>{contacts.length}</strong> talep</span>
          )}
        </p>
        {statusFilter && (
          <button
            onClick={() => setStatusFilter('')}
            className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
          >
            ✕ Filtreyi Temizle
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredContacts.length === 0 ? (
          <p className="p-6 text-center text-gray-500">
            {statusFilter ? 'Bu durumda talep bulunamadı' : 'Henüz iletişim talebi yok'}
          </p>
        ) : (
          <div className="divide-y">
            {filteredContacts.map((contact) => {
              const badge = getStatusBadge(contact.status);
              return (
              <div key={contact.id} className="p-6 hover:bg-gray-50 transition">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-bold text-lg text-gray-800">{contact.customer_name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.class}`}>
                        {badge.icon} {badge.text}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2">{contact.message}</p>
                    <div className="flex gap-4 text-sm text-gray-500">
                      {contact.customer_email && <span>📧 {contact.customer_email}</span>}
                      {contact.customer_phone && <span>📱 {contact.customer_phone}</span>}
                      <span>📅 {new Date(contact.created_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={contact.status}
                      onChange={(e) => updateStatus(contact.id, e.target.value)}
                      className="px-3 py-1 border rounded text-sm"
                    >
                      <option value="pending">Beklemede</option>
                      <option value="contacted">İletişime Geçildi</option>
                      <option value="resolved">Çözüldü</option>
                    </select>
                    <button
                      onClick={() => deleteContact(contact.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== KATEGORİ YÖNETİMİ ====================
const CATEGORY_ICONS = ['📁', '📂', '🗂️', '📝', '💻', '📚', '🛒', '📦', '🎨', '🔧', '📱', '🖥️', '✏️', '📓', '🖊️', '📋', '🎮', '🎧', '📸', '🔌'];

function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', icon: '📁', parent_id: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', icon: '📁', parent_id: '' });
    setEditingCategory(null);
    setShowForm(false);
  };

  const startEdit = (category) => {
    setFormData({
      name: category.name,
      slug: category.slug,
      icon: category.icon || '📁',
      parent_id: category.parent_id || ''
    });
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, parent_id: formData.parent_id || null };
      if (editingCategory) {
        await apiClient.put(`/categories/${editingCategory.id}`, data);
      } else {
        await apiClient.post('/categories', data);
      }
      resetForm();
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Bu kategoriyi ve alt kategorilerini silmek istediğinize emin misiniz?')) return;
    try {
      await apiClient.delete(`/categories/${id}`);
      fetchCategories();
    } catch (error) {
      console.error('Kategori silinemedi:', error);
    }
  };

  // Hiyerarşik görünüm için kategori ağacı
  const getCategoryDepth = (categoryId, depth = 0) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category || !category.parent_id) return depth;
    return getCategoryDepth(category.parent_id, depth + 1);
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const depthA = getCategoryDepth(a.id);
    const depthB = getCategoryDepth(b.id);
    if (a.parent_id === b.parent_id) return a.display_order - b.display_order;
    return depthA - depthB;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-3xl font-bold text-gray-800">📁 Kategori Yönetimi</h3>
        <button
          onClick={() => showForm ? resetForm() : setShowForm(true)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          {showForm ? '✕ İptal' : '+ Yeni Kategori'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-xl font-bold mb-4">{editingCategory ? '✏️ Kategori Düzenle' : 'Yeni Kategori Oluştur'}</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">İkon</label>
                <div className="flex flex-wrap gap-1 p-2 border rounded-lg max-h-20 overflow-y-auto">
                  {CATEGORY_ICONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`w-7 h-7 text-sm flex items-center justify-center rounded hover:bg-gray-100 ${
                        formData.icon === icon ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Kategori Adı</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                  placeholder="Kategori adı"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Slug (URL)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="kategori-slug"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Üst Kategori</label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Ana Kategori (Üst yok)</option>
                  {categories
                    .filter(c => !editingCategory || c.id !== editingCategory.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {'—'.repeat(getCategoryDepth(cat.id))} {cat.icon} {cat.name}
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>
            <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              {editingCategory ? '✓ Güncelle' : '✓ Oluştur'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <p className="p-6 text-center text-gray-500">Yükleniyor...</p>
        ) : categories.length === 0 ? (
          <p className="p-6 text-center text-gray-500">Henüz kategori yok</p>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Kategori</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Üst Kategori</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Alt Kategori</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Sayfa</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedCategories.map((category) => (
                <tr key={category.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{category.icon}</span>
                      <div>
                        <p className="font-semibold text-gray-800">{category.name}</p>
                        <p className="text-xs text-gray-500">/{category.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {category.parent_name ? (
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm">{category.parent_name}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                      {category.child_count || 0} alt
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {category.page_count || 0} sayfa
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center space-x-2">
                    <button
                      onClick={() => startEdit(category)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                    >
                      ✏️ Düzenle
                    </button>
                    <button
                      onClick={() => deleteCategory(category.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      🗑️ Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
